import { Platform } from 'react-native';

import { isVaultReadyToUse, isWrappedMasterKeyEnrolled, getCachedMasterKey } from '../services/encryption/MasterKeyService';

/**
 * Decide whether the post-login / post-auth flow needs to route
 * through UnlockVaultScreen before reaching Dashboard.
 *
 * Returns `true` when the user has enrolled a wrapped master key but
 * the device-cached unwrapped copy is missing — i.e., new device, or
 * keychain cleared, or the user is back after a full logout.
 *
 * Returns `false` when either (a) the user has never enrolled (legacy
 * path still works) or (b) enrolled AND cached (vault is ready to use
 * immediately). The caller routes to Dashboard in both cases.
 *
 * Non-throwing: any failure in the check (network error on the
 * enrollment probe, keychain unavailable) returns `false` so the user
 * isn't stranded on a lock screen because of a transient issue. The
 * downstream DocumentEncryptionService.getMasterRecoveryKey has its
 * own VaultLockedError gate that'll fire at actual document-encrypt
 * time if the check was wrong.
 */
export async function shouldRouteToUnlock(): Promise<boolean> {
  try {
    const enrolled = await isWrappedMasterKeyEnrolled();
    if (!enrolled) return false;
    const cached = await getCachedMasterKey();
    return cached === null;
  } catch {
    return false;
  }
}

/**
 * The same question asked at sign-in (Login, Splash, the authenticator
 * screen). On the phone the answer is `shouldRouteToUnlock()`.
 *
 * On the web it is always `false` (owner decision 2026-09-18): a browser
 * never keeps the unwrapped key, so the sign-in check would ask for the
 * recovery code on every visit, like a second password, to reach letters
 * and tasks that do not need it. The web asks at the moment something does
 * need the key instead (a letter recording; documents are phone-only),
 * through `shouldRouteToUnlock()` / `promptToUnlockVault` in those screens.
 */
export async function shouldRouteToUnlockAtSignIn(): Promise<boolean> {
  if (Platform.OS === 'web') return false;
  return shouldRouteToUnlock();
}

export { isVaultReadyToUse };
